import { Injectable, inject } from '@angular/core';
import { ProductDisplayName, ProductUid } from '@constants';
import { environment } from '@environments/environment';
import { BeyondRiseVideo, DoceboEnrollment, DoceboSubscription, UserService } from '@services';
import { EducationCoursesCard } from './education-courses-card.interface';
import { DoceboEnrollmentStatus, DoceboLearningType, EducationCoursesCallToAction } from './enums';

@Injectable()
export class EducationCoursesCardAdapter {
    private userService = inject(UserService);

    /**
     * Converts the given subscription from Docebo to an education courses card
     * @param subscription to convert
     * @returns the education courses card, or null
     */
    fromSubscription(subscription: DoceboSubscription): EducationCoursesCard | null {
        if (!subscription?.id || !subscription?.name) {
            console.error('Invalid Docebo subscription', subscription);
            return null;
        }

        let subscriptionTitle: string;
        let subtitleIcon: string;
        let customImageClassName: string;
        let productParam: string;

        if (subscription.name.includes('Genesys Cloud')) {
            subscriptionTitle = ProductDisplayName[ProductUid.GENESYS_CLOUD_CX];
            subtitleIcon = 'content-tags-genesys-cloud-cx.svg';
            customImageClassName = 'beyond-subscription-genesys-cloud-cx';
            productParam = 'Genesys Cloud';
        } else if (subscription.name.includes('Genesys Engage')) {
            subscriptionTitle = ProductDisplayName[ProductUid.GENESYS_ENGAGE_ON_PREM];
            subtitleIcon = 'content-tags-genesys-on-prem.svg';
            customImageClassName = 'beyond-subscription-genesys-engage-on-prem';
            productParam = 'Genesys Engage';
        }

        return {
            id: subscription.id,
            openInModal: false,
            title: `${subscriptionTitle} Learning Subscription`,
            subtitle: ['subscription', subscriptionTitle],
            subtitleIcon: `/assets/icons/beyond/${subtitleIcon}`,
            description: subscription.description,
            url: `${environment.education.url}/search;product=${encodeURIComponent(productParam)}`,
            cta: 'Explore the course catalog',
            customImageClassName: `beyond-subscription ${customImageClassName}`,
        } as EducationCoursesCard;
    }

    /**
     * Converts the given enrollment from Docebo to an education courses card
     * @param enrollment to convert
     * @param userRole user role for determining access
     * @param isEmployee whether the user is an employee
     * @returns the education courses card, or null
     */
    fromEnrollment(enrollment: DoceboEnrollment, isEmployee: boolean): EducationCoursesCard | null {
        if (!enrollment || !enrollment?.id) {
            console.error('Invalid Docebo enrollment', enrollment);
            return null;
        }

        const featureFlagGenEd = environment.featureFlags.genEd;

        return {
            id: enrollment.id,
            openInModal: false,
            title: enrollment.name,
            subtitle: [
                (enrollment.type?.toLowerCase() === 'learning_plan' ? 'course' : enrollment.type.toLowerCase()) || '',
            ],
            subtitleIcon: `/assets/icons/beyond/content-tags-${enrollment?.type?.toLowerCase()}.svg`,
            description: enrollment.description,
            url:
                featureFlagGenEd && isEmployee
                    ? environment.education.docebo.genedurl + enrollment.url
                    : environment.education.docebo.beyondurl + enrollment.url,
            cta: this.convertToEnrollmentMessage(
                enrollment.status as DoceboEnrollmentStatus,
                enrollment.type as DoceboLearningType,
            ),
            customImageClassName:
                (enrollment.type?.toLowerCase() === 'learning_plan' ? 'course' : enrollment.type.toLowerCase()) || '',
        } as EducationCoursesCard;
    }

    /**
     * Return enrolled message based on enrollment status and learningType
     * Learning type is optional. If there is no learning type, then the enrollment
     * message can still be returned if there is a valid enrollment status.
     * @param {DoceboEnrollmentStatus} status to convert
     * @param {DoceboLearningType} learningType statuses may map to a different message based on learning type
     * @throws when the given status is not being handled
     */
    private convertToEnrollmentMessage(status: DoceboEnrollmentStatus, learningType: DoceboLearningType): string {
        // First determine which action group this learning type belongs to
        let actionGroup: 'review' | 'view' | 'details';

        switch (learningType) {
            case DoceboLearningType.E_LEARNING:
            case DoceboLearningType.SELF_STUDY:
                actionGroup = 'review';
                break;

            case DoceboLearningType.INSTRUCTOR_LED_VIRTUAL:
            case DoceboLearningType.INSTRUCTOR_LED_IN_PERSON:
            case DoceboLearningType.INSTRUCTOR_LED:
            case DoceboLearningType.VIRTUAL:
            case DoceboLearningType.IN_PERSON:
                actionGroup = 'view';
                break;

            case DoceboLearningType.CERTIFICATION_EXAM:
                actionGroup = 'details';
                break;

            default:
                // Default for falsy learning types
                actionGroup = 'view';
        }

        // Then map the status to the appropriate action based on the group
        switch (status) {
            case DoceboEnrollmentStatus.COMPLETED:
                if (actionGroup === 'review') return EducationCoursesCallToAction.REVIEW;
                if (actionGroup === 'details') return EducationCoursesCallToAction.VIEW_DETAILS;
                return EducationCoursesCallToAction.VIEW;

            case DoceboEnrollmentStatus.IN_PROGRESS:
                if (actionGroup === 'details') return EducationCoursesCallToAction.VIEW_DETAILS;
                return EducationCoursesCallToAction.CONTINUE;

            case DoceboEnrollmentStatus.ENROLLED:
                if (actionGroup === 'details') return EducationCoursesCallToAction.VIEW_DETAILS;
                return EducationCoursesCallToAction.START;

            case DoceboEnrollmentStatus.WAITING:
                if (actionGroup === 'details') return EducationCoursesCallToAction.VIEW_DETAILS;
                return EducationCoursesCallToAction.WAITLIST;

            default:
                throw this.throwUnhandledStatusError(status, learningType);
        }
    }

    private throwUnhandledStatusError(status: DoceboEnrollmentStatus, learningType: DoceboLearningType) {
        return new Error(`Unhandled enrollment status '${status}' for learning type '${learningType}'.`);
    }

    /**
     * Converts the given free video from Rise to a Beyond Training Tile
     * @param freeVideo to convert
     * @returns the beyond training tile, or null
     */
    fromFreeVideo(freeVideo: BeyondRiseVideo): EducationCoursesCard | null {
        if (!freeVideo || !freeVideo?.id) {
            console.error('Invalid Beyond video', freeVideo);
            return null;
        }

        return {
            id: freeVideo.id,
            openInModal: true,
            title: freeVideo.title?.rendered || '',
            description: freeVideo.content?.rendered || '',
            subtitle: ['video'],
            subtitleIcon: '/assets/icons/content-tags-video.svg',
            url: freeVideo.acf?.embed || '',
            duration: freeVideo.acf?.duration,
            cta: EducationCoursesCallToAction.WATCH,
            desktopThumbnail: freeVideo.acf?.thumbnails?.desktop || '',
            mobileThumbnail: freeVideo.acf?.thumbnails?.mobile || '',
        } as EducationCoursesCard;
    }
}
